// src/components/AuthCallback.tsx
//
// Handles the redirect from the backend after a successful Easy Auth +
// JWT mint. The backend lands the browser here with auth data in the URL
// fragment (e.g. /auth/callback#jwt=...&name=...&email=...&roles=...&organizationIds=...).
// We extract it, persist it, and navigate to /fleet (replace, so the fragment
// is wiped from history).

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import useAuth from '../hooks/useAuth.ts';
import logoAuth from '../Images/logo.svg';
import styles from './login.module.css';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const {
    setAuthenticated,
    setName,
    setEmail,
    setRoles,
    setOrganizationIds,
  } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    try {
      const rawHash = window.location.hash || '';
      const hash = rawHash.startsWith('#') ? rawHash.slice(1) : rawHash;
      const params = new URLSearchParams(hash);

      const errParam = params.get('error');
      if (errParam) {
        setError(decodeURIComponent(params.get('error_description') || errParam));
        const t = setTimeout(() => navigate('/sign-in', { replace: true }), 2500);
        return () => clearTimeout(t);
      }

      const jwt = params.get('jwt');
      const name = params.get('name');
      const email = params.get('email');
      const rolesStr = params.get('roles');
      const orgIdsStr = params.get('organizationIds');

      if (!jwt || !name || !email) {
        setError('Missing authentication data in callback');
        const t = setTimeout(() => navigate('/sign-in', { replace: true }), 2500);
        return () => clearTimeout(t);
      }

      const roles = rolesStr ? JSON.parse(rolesStr) : [];
      const organizationIds = orgIdsStr ? JSON.parse(orgIdsStr) : [];

      localStorage.setItem('token', jwt);
      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('name', name);
      localStorage.setItem('email', email);
      localStorage.setItem('roles', JSON.stringify(roles));
      localStorage.setItem('organizationIds', JSON.stringify(organizationIds));

      axios.defaults.headers.common['Authorization'] = `Bearer ${jwt}`;

      setAuthenticated(true);
      setName(name);
      setEmail(email);
      setRoles(roles);
      setOrganizationIds(organizationIds);

      navigate('/fleet', { replace: true });
    } catch (e) {
      console.error('Auth callback failed:', e);
      setError('Failed to process authentication');
      const t = setTimeout(() => navigate('/sign-in', { replace: true }), 2500);
      return () => clearTimeout(t);
    }
  }, [navigate, setAuthenticated, setName, setEmail, setRoles, setOrganizationIds]);

  return (
    <section className={styles.section}>
      <div className={styles.signIn}>
        <div className={styles.logo}>
          <img src={logoAuth} alt="logo" className={styles.user_img} />
        </div>
        {error ? (
          <div style={{
            padding: '10px',
            marginTop: '20px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '4px',
            fontSize: '14px',
          }}>
            {error}
          </div>
        ) : (
          <p style={{ marginTop: '20px' }}>Completing sign-in…</p>
        )}
      </div>
    </section>
  );
};

export default AuthCallback;
